import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '@/i18n/use-translations';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RotateCcw, SendHorizontal, Sparkles } from 'lucide-react';
import type { AgentBuilderApplyResult, AgentBuilderFocus } from '@/lib/api';
import { ProposalCard } from './proposal-card';
import { useBuilderChat } from './use-builder-chat';

/** Suggestion chips per focus (i18n keys under agents.builder.chips). */
export const FOCUS_CHIPS: Record<AgentBuilderFocus, string[]> = {
  all: ['buildPm', 'buildSupport', 'reviewAgent'],
  soul: ['moreDirect', 'warmer', 'addLimits'],
  prompt: ['improvePrompt', 'shorterAnswers', 'askBeforeActing'],
  rules: ['addGreetingRule', 'addHoursRule'],
  tools: ['whatData', 'readOnly'],
  settings: ['quietHours', 'fewerMessages', 'timezone'],
  voice: ['whichVoice', 'enableVoice'],
  playbooks: ['newPlaybook', 'shorterCalls'],
  rituals: ['fridayClosing', 'dailySummary', 'heartbeat'],
  knowledge: ['useKnowledge', 'enableMemory'],
};

interface BuilderPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Agent being edited; null for "Crear con asistente". */
  agentId: string | null;
  agentName?: string;
  focus: AgentBuilderFocus;
  onApplied?: (result: AgentBuilderApplyResult) => void;
}

export function BuilderPanel({ open, onOpenChange, agentId, agentName, focus, onApplied }: BuilderPanelProps) {
  const t = useTranslations('agents.builder');
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'es';
  const { messages, loading, error, send, retry, apply, discard, reset } = useBuilderChat({ agentId, focus, locale });
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, loading]);

  const submit = async (text = draft) => {
    if (!text.trim() || loading) return;
    setDraft('');
    await send(text);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  const title = agentId ? t('titleEdit', { agent: agentName || t('thisAgent') }) : t('titleCreate');
  const chips = FOCUS_CHIPS[focus] ?? FOCUS_CHIPS.all;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg gap-0 p-0" data-testid="builder-panel">
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            {title}
          </SheetTitle>
          <SheetDescription>{t(`focusHint.${focus}`)}</SheetDescription>
          {messages.length > 0 && (
            <div>
              <Button variant="ghost" size="xs" onClick={reset} disabled={loading}>
                <RotateCcw aria-hidden="true" />
                {t('newConversation')}
              </Button>
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4" aria-live="polite">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{agentId ? t('introEdit') : t('introCreate')}</p>
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('suggestions')}>
                {chips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => void submit(t(`chips.${chip}`))}
                    disabled={loading}
                    className="rounded-full border bg-background px-3 py-1.5 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
                  >
                    {t(`chips.${chip}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'space-y-3'}>
              <div
                className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm ${
                  m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}
              >
                <span className="sr-only">{m.role === 'user' ? t('you') : t('assistant')}: </span>
                {m.content}
              </div>
              {m.proposals?.map((p) => (
                <ProposalCard
                  key={p.proposal.id}
                  entry={p}
                  locale={locale}
                  onApply={async (ids) => {
                    const result = await apply(p.proposal, ids);
                    if (result && result.applied > 0) onApplied?.(result);
                    return result;
                  }}
                  onDiscard={() => discard(p.proposal.id)}
                  onRequestChanges={() => {
                    setDraft(t('requestChangesPrefill'));
                    inputRef.current?.focus();
                  }}
                />
              ))}
            </div>
          ))}

          {loading && (
            <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="builder-typing">
              <span className="flex gap-1" aria-hidden="true">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
              </span>
              {t('thinking')}
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-2">
              {error.code === 'AI_PROVIDER_NOT_CONFIGURED' ? (
                <>
                  <p>{t('providerMissing')}</p>
                  <Link to="/settings/ai-provider" className="underline underline-offset-4" onClick={() => onOpenChange(false)}>
                    {t('providerLink')}
                  </Link>
                </>
              ) : error.code === 'FORBIDDEN' ? (
                <p>{t('forbidden')}</p>
              ) : (
                <>
                  <p>{t('chatError', { message: error.message })}</p>
                  <Button size="xs" variant="outline" onClick={() => void retry()}>{t('retry')}</Button>
                </>
              )}
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          className="border-t p-3 space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="flex items-end gap-2">
            <label htmlFor="builder-input" className="sr-only">{t('inputLabel')}</label>
            <Textarea
              id="builder-input"
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('inputPlaceholder')}
              rows={2}
              className="min-h-[44px] max-h-40 resize-none"
              aria-describedby="builder-input-hint"
            />
            <Button type="submit" size="icon" disabled={!draft.trim() || loading} aria-label={t('send')}>
              <SendHorizontal aria-hidden="true" />
            </Button>
          </div>
          <p id="builder-input-hint" className="text-[11px] text-muted-foreground">{t('inputHint')}</p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
